import { supabase } from "@/integrations/supabase/client";
import { startPlanCheckout } from "@/lib/planBilling.functions";
import { translations } from "@/i18n/translations";

type Body = { product_type?: string; product_key?: string; billing_interval?: string; [k: string]: unknown };
const PLAN_KEYS = new Set(["basico", "club_pro", "completo"]);

/** Las cuentas del equipo (admin / admin_lectura) nunca abren pagos reales. */
export function isStaffRoleList(roles: string[]): boolean {
  return roles.some((r) => r === "admin" || r === "admin_lectura");
}

async function isStaffSession(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const uid = session?.user?.id;
    if (!uid) return false;
    const { data } = await supabase.from("user_roles").select("role").eq("user_id", uid);
    return isStaffRoleList((data ?? []).map((r: any) => String(r.role)));
  } catch {
    return false;
  }
}

function notifyStaffBlocked() {
  if (typeof window === "undefined") return;
  const lang = (localStorage.getItem("lang") === "en" ? "en" : "es") as "es" | "en";
  const t = translations.adminCheckoutGuard;
  void import("sonner").then(({ toast }) => toast.info(t.title[lang], { description: t.body[lang] }));
}

/** true (y avisa) si la sesión es del equipo: ningún flujo de pago debe seguir. */
export async function blockStaffCheckout(): Promise<boolean> {
  if (!(await isStaffSession())) return false;
  try { localStorage.removeItem("pending_checkout"); } catch { /* sin navegador */ }
  notifyStaffBlocked();
  return true;
}

/**
 * Punto único de pago. El Club Pro antiguo ("pro") y los planes nuevos pasan
 * siempre por el pago de planes, que aplica la prueba de 7 días (una sola vez
 * por persona). El resto (mentoría, música por local, propuestas...) sigue
 * por el pago existente. Las cuentas del equipo quedan bloqueadas aquí.
 */
export async function invokeCheckout({ body }: { body: Body }): Promise<{ data: any; error: any }> {
  if (await isStaffSession()) {
    try { localStorage.removeItem("pending_checkout"); } catch { /* sin navegador */ }
    notifyStaffBlocked();
    return { data: null, error: { message: "staff_checkout_blocked", silent: true } };
  }
  const type = body?.product_type;
  const key = body?.product_key;
  const planKey = type === "pro" ? "club_pro" : type === "plan" && key && PLAN_KEYS.has(key) ? key : null;
  if (planKey) {
    try {
      const r = await startPlanCheckout({ data: { plan_key: planKey as any, interval: "month", source: "pricing" } });
      return { data: { url: r.url, trial: r.trial }, error: null };
    } catch (e: any) {
      return { data: null, error: { message: e?.message || String(e) } };
    }
  }
  return supabase.functions.invoke("create-checkout", { body });
}
